import type { UseMutationOptions } from '@tanstack/react-query';

/**
 * How long a change of access takes to reach compute: it caches its decision for 30 s per token (compute#21), and 27-30 s was
 * measured on staging (R4b). Every dialog and toast that promises a delay (revoke, grant, move to a project) says these words.
 */
const PROPAGATION_LAG_SECONDS = 30;
const PROPAGATION_LAG = `about ${PROPAGATION_LAG_SECONDS} seconds`;
/** The same lag as a number of milliseconds, for a refetch that must wait for compute's cache to expire. */
export const PROPAGATION_LAG_MS = PROPAGATION_LAG_SECONDS * 1000;
export const REVOKE_DONE_TEXT = `It can take ${PROPAGATION_LAG} to take effect.`;
export const REVOKE_DIALOG_LAG = `Access stops within ${PROPAGATION_LAG}.`;
export const GRANT_DONE_TEXT = `It takes effect within ${PROPAGATION_LAG}.`;

/** The toast line after resources moved to a project: people with a role there reach them (or it) within the lag. */
export function moveReachText(plural: boolean): string {
  return `People with a role on the project reach ${plural ? 'them' : 'it'} within ${PROPAGATION_LAG}.`;
}

/**
 * Sends the DELETE of `bindingId` once while one is in flight; a second call (a double click: `isPending` is read from the
 * last render, so both clicks land before it turns true, R4b: 2 DELETEs, both 200) gets the first call's promise. The map is
 * written synchronously and cleared when the request settles, success or failure.
 */
export function deleteOnce(inFlight: Map<string, Promise<void>>, bindingId: string, send: () => Promise<void>): Promise<void> {
  const running = inFlight.get(bindingId);
  if (running) return running;
  const call = send().finally(() => inFlight.delete(bindingId));
  inFlight.set(bindingId, call);
  return call;
}

/** What `onMutate` hands to the other callbacks: whether this call is the one that sends the DELETE. */
export interface RevokeCall {
  sender: boolean;
}

/**
 * The options of the revoke mutation. Two clicks on one binding share one DELETE (`deleteOnce`), and only the call that sent
 * it runs the caller's `onMutate`, `onSuccess`, `onError` and `onSettled`: a double click on a failing DELETE gives one error
 * toast, not two (R4c). The first `onMutate` of a binding claims it (calls start in click order); the claim is released when
 * the sender settles, success or failure. The list refresh runs for every call.
 */
export function revokeMutationOptions(
  deps: { inFlight: Map<string, Promise<void>>; claims: Set<string>; send: (bindingId: string) => Promise<void>; invalidate: () => void },
  options?: UseMutationOptions<void, unknown, string, unknown>,
): UseMutationOptions<void, unknown, string, RevokeCall> {
  const { inFlight, claims, send, invalidate } = deps;
  return {
    ...options,
    mutationFn: (bindingId) => deleteOnce(inFlight, bindingId, () => send(bindingId)),
    onMutate: async (bindingId, context) => {
      const sender = !claims.has(bindingId);
      claims.add(bindingId);
      if (sender) await options?.onMutate?.(bindingId, context);
      return { sender };
    },
    onSuccess: (data, bindingId, call, context) => {
      invalidate();
      if (call?.sender) options?.onSuccess?.(data, bindingId, undefined, context);
    },
    onError: (error, bindingId, call, context) => {
      if (call?.sender) options?.onError?.(error, bindingId, undefined, context);
    },
    onSettled: (data, error, bindingId, call, context) => {
      if (!call?.sender) return;
      claims.delete(bindingId);
      options?.onSettled?.(data, error, bindingId, undefined, context);
    },
  };
}
